import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Smartphone, Bell, MapPin, WifiOff, TrendingUp, 
  Apple, Layers, CheckCircle, X, 
  ArrowRight, Code, Cpu, Shield, Zap, Home, Search, User, Send
} from 'lucide-react';

// --- SUCCESS POPUP (Clean White) ---
const SuccessPopup = ({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) => {
  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-[70] flex justify-center items-center px-4">
          {/* Backdrop */}
          <motion.div 
            initial={{ opacity: 0 }} 
            animate={{ opacity: 1 }} 
            exit={{ opacity: 0 }}
            className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm"
            onClick={onClose}
          />
          {/* Popup Card */}
          <motion.div 
            initial={{ scale: 0.8, opacity: 0, y: 20 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.9, opacity: 0, y: 20 }}
            transition={{ type: "spring", stiffness: 300, damping: 25 }}
            className="bg-white rounded-2xl p-8 w-full max-w-sm text-center shadow-2xl relative z-10"
          >
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4 border border-green-200">
              <CheckCircle size={32} className="text-green-600" />
            </div>
            <h3 className="text-xl font-bold text-slate-900 mb-2">Request Sent!</h3>
            <p className="text-slate-500 mb-6 text-sm">
              We have received your details. Our tech team will connect with you shortly.
            </p>
            <button 
              onClick={onClose} 
              className="w-full bg-slate-900 hover:bg-slate-800 text-white py-3 rounded-xl font-semibold transition shadow-lg"
            >
              Close
            </button>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

const WithApp: React.FC = () => {
  // --- STATE MANAGEMENT ---
  const [isQuoteModalOpen, setIsQuoteModalOpen] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Form State
  const [formData, setFormData] = useState({
    name: '',
    companyName: '',
    phone: '',
    email: '',
    projectBrief: ''
  });

  const openModal = () => setIsQuoteModalOpen(true);
  const closeModal = () => setIsQuoteModalOpen(false);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    const payload = { ...formData, serviceType: "App Development" };

    // API Call Simulation
    fetch('https://www.projectinfotech.in/api/quotation/submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
    }).catch(err => console.error("Background submission error:", err));

    // Simulate delay
    setTimeout(() => {
        setIsSubmitting(false);
        setFormData({ name: '', companyName: '', phone: '', email: '', projectBrief: '' });
        closeModal();
        setShowSuccess(true);
    }, 800);
  };

  // --- ANIMATION VARIANTS ---
  const fadeIn = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
  };

  const floating = {
    animate: {
      y: [0, -20, 0],
      transition: { duration: 5, repeat: Infinity, ease: "easeInOut" }
    }
  };

  const floatingDelayed = {
    animate: {
      y: [0, -15, 0],
      transition: { duration: 6, repeat: Infinity, ease: "easeInOut", delay: 1 }
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900 font-sans overflow-x-hidden selection:bg-red-500 selection:text-white">
      
      {/* ================= HERO SECTION ================= */}
      <section className="relative w-full pt-32 pb-24 px-6 bg-[#0B0F19] text-white overflow-hidden">
        {/* Background Glows */}
        <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-blue-600/20 blur-[150px] rounded-full pointer-events-none" />
        <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-red-600/10 blur-[150px] rounded-full pointer-events-none" />

        <div className="container mx-auto max-w-7xl relative z-10 flex flex-col lg:flex-row items-center gap-16">
          
          {/* LEFT CONTENT */}
          <motion.div 
            initial="hidden" animate="visible" variants={fadeIn}
            className="lg:w-1/2 space-y-8"
          >
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-slate-800 border border-slate-700">
               <Smartphone className="w-4 h-4 text-blue-400" />
               <span className="text-xs font-bold tracking-widest text-slate-300 uppercase">iOS & Android Solutions</span>
            </div>

            <h1 className="text-4xl md:text-6xl font-extrabold leading-tight font-aligran">
              Put Your Business in <br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-purple-500">
                Every Pocket
              </span>
            </h1>
            
            <p className="text-lg text-slate-400 max-w-xl leading-relaxed">
              Websites wait for visitors. Apps live with them. 
              We build high-performance <strong>Native</strong> and <strong>Hybrid Apps</strong> that drive engagement and loyalty.
            </p>

            <div className="flex flex-wrap gap-4">
              <button onClick={openModal} className="px-8 py-4 bg-red-600 hover:bg-red-700 rounded-xl font-bold shadow-lg shadow-red-900/20 flex items-center gap-2 transition-all">
                 Start Your App <ArrowRight className="w-5 h-5" />
              </button>
            </div>
          </motion.div>

          {/* --- RIGHT SIDE: FLOATING PHONES MOTION --- */}
          {/* Centered Graphics */}
          <div className="lg:w-1/2 relative h-[550px] w-full perspective-1000 flex justify-center items-center">
            
            {/* 1. Main Phone (Center - Consumer App UI) */}
            <motion.div 
            //   variants={floating}
              animate="animate"
              className="relative w-[280px] h-[540px] bg-slate-900 rounded-[3rem] border-[8px] border-slate-800 shadow-2xl z-20 overflow-hidden"
            >
              <div className="absolute top-0 left-1/2 -translate-x-1/2 w-32 h-6 bg-slate-800 rounded-b-xl z-30" />
              <div className="w-full h-full bg-[#0f172a] pt-10 pb-4 px-4 flex flex-col relative">
                 <div className="flex justify-between items-center mb-6">
                    <div className="flex items-center gap-2">
                       <div className="w-8 h-8 rounded-full bg-red-500 flex items-center justify-center font-bold text-white text-xs">P</div>
                       <div className="text-sm font-bold text-white">PIT Courses</div>
                    </div>
                    <Bell className="w-5 h-5 text-slate-400" />
                 </div>
                 
                 <div className="w-full h-40 bg-gradient-to-br from-blue-600 to-purple-600 rounded-2xl mb-6 p-4 text-white relative overflow-hidden">
                    <div className="absolute -right-4 -bottom-4 w-20 h-20 bg-white/20 rounded-full" />
                    <h4 className="font-bold text-lg mb-1">Master Coding</h4>
                    <p className="text-xs text-blue-100 mb-3">50% Off Limited Time</p>
                    <button className="bg-white text-blue-600 text-xs px-3 py-1.5 rounded-lg font-bold">Enroll Now</button>
                 </div>

                 <div className="space-y-3 flex-1 overflow-hidden">
                    {[1, 2, 3].map(i => (
                       <div key={i} className="flex items-center gap-3 p-3 bg-slate-800/50 rounded-xl border border-slate-700/50">
                          <div className="w-10 h-10 rounded-lg bg-slate-700" />
                          <div className="flex-1">
                             <div className="h-2 w-20 bg-slate-600 rounded mb-1.5" />
                             <div className="h-2 w-12 bg-slate-700 rounded" />
                          </div>
                       </div>
                    ))}
                 </div>

                 <div className="h-16 border-t border-slate-800 flex justify-around items-center text-slate-500 mt-2">
                    <Home className="w-6 h-6 text-blue-500" />
                    <Search className="w-6 h-6" />
                    <User className="w-6 h-6" />
                 </div>
              </div>
            </motion.div>

            {/* 2. Secondary Phone (Behind) */}
            <motion.div 
            //    variants={floatingDelayed}
               animate="animate"
               className="absolute top-12 -right-4 lg:right-10 w-[260px] h-[500px] bg-slate-800 rounded-[2.5rem] border-[8px] border-slate-700 shadow-xl z-10 opacity-60 scale-95 blur-[1px]"
            >
               <div className="w-full h-full bg-slate-900 p-6 pt-12">
                  <div className="h-4 w-1/2 bg-slate-700 rounded mb-6" />
                  <div className="grid grid-cols-2 gap-3 mb-6">
                     <div className="h-20 bg-slate-700 rounded-xl" />
                     <div className="h-20 bg-slate-700 rounded-xl" />
                  </div>
                  <div className="h-32 bg-slate-700 rounded-xl w-full" />
               </div>
            </motion.div>

            {/* 3. Notification Bubble */}
            <motion.div 
               animate={{ y: [0, 10, 0] }}
               transition={{ duration: 4, repeat: Infinity }}
               className="absolute top-40 -left-6 lg:left-0 bg-white p-3 rounded-xl shadow-xl flex items-center gap-3 z-30 max-w-[180px]"
            >
               <div className="bg-green-100 p-2 rounded-full text-green-600">
                  <CheckCircle size={18} />
               </div>
               <div>
                  <p className="text-xs font-bold text-slate-800">App Published</p>
                  <p className="text-[10px] text-slate-500">Successfully on Play Store</p>
               </div>
            </motion.div>

             {/* 4. Code Snippet */}
             <motion.div 
               animate={{ y: [0, -10, 0] }}
               transition={{ duration: 6, repeat: Infinity, delay: 2 }}
               className="absolute bottom-20 -right-2 lg:right-6 bg-[#1E1E1E] p-4 rounded-xl border border-slate-600 shadow-2xl z-30"
            >
               <div className="flex gap-1.5 mb-2 opacity-50 border-b border-white/10 pb-2">
                 <div className="text-[10px] text-slate-400">App.tsx</div>
               </div>
               <div className="font-mono text-[10px] text-blue-300">
                  <p><span className="text-pink-400">const</span> App = () =&gt; &#123;</p>
                  <p className="pl-2 text-white">&lt;NativeView&gt;</p>
                  <p className="pl-4 text-green-400">"Hello World"</p>
                  <p className="pl-2 text-white">&lt;/NativeView&gt;</p>
                  <p>&#125;</p>
               </div>
            </motion.div>

          </div>
        </div>
      </section>

      {/* ================= PLATFORMS SECTION ================= */}
      <section className="py-20 px-6 bg-white">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-16">
             <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4 font-aligran">We Cover Every Device</h2>
             <div className="w-20 h-1.5 bg-red-600 mx-auto rounded-full"/>
             <p className="text-slate-600 mt-6 max-w-2xl mx-auto">
               From native performance to cross-platform efficiency, we choose the right tech for your goal.
             </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
             <div className="bg-slate-50 p-8 rounded-2xl border border-slate-200 hover:shadow-xl transition-all group">
                <div className="w-14 h-14 bg-green-100 rounded-xl flex items-center justify-center text-green-600 mb-6 group-hover:bg-green-600 group-hover:text-white transition-colors">
                   <Smartphone className="w-8 h-8" />
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-3">Android Native</h3>
                <p className="text-slate-600 text-sm mb-4">Built with Kotlin/Java. Access full device hardware like Camera and GPS seamlessly.</p>
                <div className="flex flex-wrap gap-2">
                   <span className="px-2 py-1 bg-white border text-xs font-bold text-slate-500 rounded">Kotlin</span>
                   <span className="px-2 py-1 bg-white border text-xs font-bold text-slate-500 rounded">Play Store</span>
                </div>
             </div>

             <div className="bg-slate-50 p-8 rounded-2xl border border-slate-200 hover:shadow-xl transition-all group">
                <div className="w-14 h-14 bg-slate-200 rounded-xl flex items-center justify-center text-slate-800 mb-6 group-hover:bg-slate-800 group-hover:text-white transition-colors">
                   <Apple className="w-8 h-8" />
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-3">iOS Native</h3>
                <p className="text-slate-600 text-sm mb-4">Premium apps using Swift. Smooth animations and strict security for Apple users.</p>
                <div className="flex flex-wrap gap-2">
                   <span className="px-2 py-1 bg-white border text-xs font-bold text-slate-500 rounded">Swift</span>
                   <span className="px-2 py-1 bg-white border text-xs font-bold text-slate-500 rounded">App Store</span>
                </div>
             </div>

             <div className="bg-slate-50 p-8 rounded-2xl border border-slate-200 hover:shadow-xl transition-all group">
                <div className="w-14 h-14 bg-blue-100 rounded-xl flex items-center justify-center text-blue-600 mb-6 group-hover:bg-blue-600 group-hover:text-white transition-colors">
                   <Layers className="w-8 h-8" />
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-3">Flutter Hybrid</h3>
                <p className="text-slate-600 text-sm mb-4">One code, two platforms. Save 40% cost and time without compromising quality.</p>
                <div className="flex flex-wrap gap-2">
                   <span className="px-2 py-1 bg-white border text-xs font-bold text-slate-500 rounded">Flutter</span>
                   <span className="px-2 py-1 bg-white border text-xs font-bold text-slate-500 rounded">React Native</span>
                </div>
             </div>
          </div>
        </div>
      </section>

      {/* ================= FEATURES GRID ================= */}
      <section className="py-20 px-6 bg-slate-900 text-white">
        <div className="container mx-auto max-w-6xl">
            <div className="flex flex-col md:flex-row items-center gap-12">
               <div className="md:w-1/2">
                  <h2 className="text-3xl font-bold mb-6 font-aligran">Why Apps Grow Business?</h2>
                  <p className="text-slate-400 mb-8 leading-relaxed">
                     90% of mobile time is spent in apps, not browsers. Apps allow you to send notifications directly to your customer's pocket.
                  </p>
                  <ul className="space-y-4">
                     {[
                        {icon: Bell, text: "Push Notifications for Marketing"},
                        {icon: WifiOff, text: "Works Offline without Internet"},
                        {icon: Shield, text: "Biometric Security (FaceID/Fingerprint)"},
                        {icon: Zap, text: "3x Faster than Mobile Websites"}
                     ].map((item, idx) => (
                        <li key={idx} className="flex items-center gap-4 bg-slate-800 p-4 rounded-xl border border-slate-700">
                           <item.icon className="text-blue-400 w-6 h-6" />
                           <span className="font-semibold">{item.text}</span>
                        </li>
                     ))}
                  </ul>
               </div>
               <div className="md:w-1/2 relative">
                  <div className="absolute inset-0 bg-gradient-to-tr from-red-600/20 to-blue-600/20 rounded-full blur-3xl" />
                  <div className="relative bg-slate-800 border border-slate-700 p-8 rounded-3xl shadow-2xl">
                     <h3 className="text-3xl font-bold mb-6 text-center font-aligran">Development Stack</h3>
                     <div className="grid grid-cols-2 gap-4">
                        <div className="bg-slate-900 p-4 rounded-lg flex flex-col items-center">
                           <Code className="text-blue-400 mb-2"/> <span className="text-sm">React Native</span>
                        </div>
                        <div className="bg-slate-900 p-4 rounded-lg flex flex-col items-center">
                           <Layers className="text-cyan-400 mb-2"/> <span className="text-sm">Flutter</span>
                        </div>
                        <div className="bg-slate-900 p-4 rounded-lg flex flex-col items-center">
                           <Cpu className="text-green-400 mb-2"/> <span className="text-sm">Node.js API</span>
                        </div>
                        <div className="bg-slate-900 p-4 rounded-lg flex flex-col items-center">
                           <Shield className="text-orange-400 mb-2"/> <span className="text-sm">Firebase</span>
                        </div>
                     </div>
                  </div>
               </div>
            </div>
        </div>
      </section>

      {/* ================= CTA SECTION ================= */}
      <section className="py-24 bg-white border-t border-slate-200 text-center px-6">
        <div className="max-w-3xl mx-auto">
           <h2 className="text-5xl font-bold text-slate-900 mb-6 font-aligran">Have an App Idea?</h2>
           <p className="text-slate-600 text-lg mb-10">
             Let's discuss how we can bring it to life on the screens of millions of users. 
             Get a free consultation today.
           </p>
           <button 
             onClick={openModal}
             className="px-10 py-5 bg-red-600 hover:bg-red-700 text-white font-bold rounded-xl shadow-xl shadow-red-600/20 transition-all text-lg flex items-center justify-center gap-3 mx-auto"
           >
              <Smartphone className="w-6 h-6" /> Request App Quote
           </button>
        </div>
      </section>

      {/* ================= CLEAN WHITE MODAL ================= */}
      <AnimatePresence>
        {isQuoteModalOpen && (
          <div className="fixed inset-0 z-[60] flex justify-center items-center px-4">
              
              {/* Backdrop */}
              <motion.div 
                initial={{ opacity: 0 }} 
                animate={{ opacity: 1 }} 
                exit={{ opacity: 0 }}
                onClick={closeModal}
                className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm"
              />

              {/* Modal Content */}
              <motion.div 
                 initial={{ opacity: 0, scale: 0.9, y: 20 }}
                 animate={{ opacity: 1, scale: 1, y: 0 }}
                 exit={{ opacity: 0, scale: 0.9, y: 20 }}
                 transition={{ type: "spring", stiffness: 300, damping: 25 }}
                 className="bg-white border border-slate-100 w-full max-w-md rounded-2xl shadow-2xl relative z-10 overflow-hidden"
              >
                  {/* Header */}
                  <div className="px-6 py-4 border-b border-slate-100 flex justify-between items-center bg-slate-50">
                    <div>
                      <h3 className="text-lg font-bold text-slate-900 flex items-center gap-2">
                        <Smartphone className="w-5 h-5 text-red-600"/> App Inquiry
                      </h3>
                      <p className="text-xs text-slate-500">Get a free quote within 24 hours.</p>
                    </div>
                    <button onClick={closeModal} className="text-slate-400 hover:text-red-500 p-1 rounded-full hover:bg-slate-100 transition">
                      <X size={20} />
                    </button>
                  </div>

                  {/* Form */}
                  <form onSubmit={handleFormSubmit} className="p-6 space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-1">
                          <label className="text-[10px] font-bold text-slate-600 uppercase tracking-wider">Name</label>
                          <input 
                            type="text" name="name" placeholder="John Doe"
                            value={formData.name} onChange={handleInputChange} required 
                            className="w-full bg-slate-50 border border-slate-200 text-slate-900 text-sm px-3 py-2.5 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500/20 focus:border-red-500 transition-all"
                          />
                        </div>
                        <div className="space-y-1">
                          <label className="text-[10px] font-bold text-slate-600 uppercase tracking-wider">Phone</label>
                          <input 
                            type="tel" name="phone" placeholder="+91..."
                            value={formData.phone} onChange={handleInputChange} required 
                            className="w-full bg-slate-50 border border-slate-200 text-slate-900 text-sm px-3 py-2.5 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500/20 focus:border-red-500 transition-all"
                          />
                        </div>
                      </div>

                      <div className="space-y-1">
                        <label className="text-[10px] font-bold text-slate-600 uppercase tracking-wider">Email</label>
                        <input 
                          type="email" name="email" placeholder="john@company.com"
                          value={formData.email} onChange={handleInputChange} required 
                          className="w-full bg-slate-50 border border-slate-200 text-slate-900 text-sm px-3 py-2.5 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500/20 focus:border-red-500 transition-all"
                        />
                      </div>

                      <div className="space-y-1">
                        <label className="text-[10px] font-bold text-slate-600 uppercase tracking-wider">Idea Brief</label>
                        <textarea 
                          name="projectBrief" rows={3} placeholder="I want an app like..."
                          value={formData.projectBrief} onChange={handleInputChange} required 
                          className="w-full bg-slate-50 border border-slate-200 text-slate-900 text-sm px-3 py-2.5 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500/20 focus:border-red-500 transition-all resize-none"
                        ></textarea>
                      </div>

                      <div className="pt-2">
                        <button 
                          type="submit" 
                          disabled={isSubmitting}
                          className="w-full bg-gradient-to-r from-red-600 to-red-700 hover:from-red-500 hover:to-red-600 text-white font-bold py-3 rounded-lg shadow-lg shadow-red-900/30 transition-all flex justify-center items-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed"
                        >
                          {isSubmitting ? (
                            <span className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"/>
                          ) : (
                            <>Submit Request <Send size={16} /></>
                          )}
                        </button>
                      </div>
                  </form>
              </motion.div>
          </div>
        )}
      </AnimatePresence>
      
      <SuccessPopup isOpen={showSuccess} onClose={() => setShowSuccess(false)} />
    </div>
  );
};

export default WithApp;